import { type SxProps, type Theme, Typography, useTheme } from '@mui/material';
import Stack from '@mui/material/Stack';

import { DeviceStatus } from '@assets/enums/device-type.emun.ts';

type DeviceStatusPillConfig = {
  label: string;
  filled: {
    getBackgroundColor: (palette: Theme['palette']) => string;
    getTextColor: (palette: Theme['palette']) => string;
  };
  outlined: {
    getBorderColor: (palette: Theme['palette']) => string;
    getTextColor: (palette: Theme['palette']) => string;
  };
};

export const DEVICE_STATUS_PILL_CONFIG: Record<DeviceStatus, DeviceStatusPillConfig> = {
  [DeviceStatus.Online]: {
    label: 'Online',
    filled: {
      getBackgroundColor: (palette) => palette.accent,
      getTextColor: (palette) => palette.white_100,
    },
    outlined: {
      getBorderColor: (palette) => palette.accent,
      getTextColor: (palette) => palette.accent,
    },
  },
  [DeviceStatus.Recording]: {
    label: 'Recording',
    filled: {
      getBackgroundColor: (palette) => palette.attention,
      getTextColor: (palette) => palette.white_100,
    },
    outlined: {
      getBorderColor: (palette) => palette.attention,
      getTextColor: (palette) => palette.attention,
    },
  },
  [DeviceStatus.Streaming]: {
    label: 'Streaming',
    filled: {
      getBackgroundColor: (palette) => palette.blue,
      getTextColor: (palette) => palette.white_100,
    },
    outlined: {
      getBorderColor: (palette) => palette.blue,
      getTextColor: (palette) => palette.blue,
    },
  },
  [DeviceStatus.Sleeping]: {
    label: 'Sleeping',
    filled: {
      getBackgroundColor: (palette) => palette.light_dark,
      getTextColor: (palette) => palette.white_100,
    },
    outlined: {
      getBorderColor: (palette) => palette.semi_dark,
      getTextColor: (palette) => palette.semi_dark,
    },
  },
  [DeviceStatus.Offline]: {
    label: 'Offline',
    filled: {
      getBackgroundColor: (palette) => palette.semi_dark,
      getTextColor: (palette) => palette.bgDark,
    },
    outlined: {
      getBorderColor: (palette) => palette.light_dark,
      getTextColor: (palette) => palette.light_dark,
    },
  },
  [DeviceStatus.Waking]: {
    label: 'Waking up',
    filled: {
      getBackgroundColor: (palette) => palette.neon_yellow_100,
      getTextColor: (palette) => palette.dark,
    },
    outlined: {
      getBorderColor: (palette) => palette.neon_yellow_100,
      getTextColor: (palette) => palette.semi_dark,
    },
  },
  [DeviceStatus.Error]: {
    label: 'Error',
    filled: {
      getBackgroundColor: (palette) => palette.error.main,
      getTextColor: (palette) => palette.white_100,
    },
    outlined: {
      getBorderColor: (palette) => palette.error.main,
      getTextColor: (palette) => palette.error.main,
    },
  },
  [DeviceStatus.Bootloader]: {
    label: 'Bootloader',
    filled: {
      getBackgroundColor: (palette) => palette.light_dark,
      getTextColor: (palette) => palette.white_100,
    },
    outlined: {
      getBorderColor: (palette) => palette.semi_dark,
      getTextColor: (palette) => palette.semi_dark,
    },
  },
};

export const DeviceStatusPill = ({
  status,
  sx,
  variant = 'filled',
}: {
  status: DeviceStatus;
  sx?: SxProps<Theme>;
  variant?: 'filled' | 'outlined';
}) => {
  const { palette } = useTheme();
  const { label, filled, outlined } = DEVICE_STATUS_PILL_CONFIG[status];
  const isOutlined = variant === 'outlined';
  const backgroundColor = isOutlined ? palette.white_100 : filled.getBackgroundColor(palette);
  const textColor = isOutlined ? outlined.getTextColor(palette) : filled.getTextColor(palette);
  return (
    <Stack
      alignItems={'center'}
      justifyContent={'center'}
      sx={{
        width: 'fit-content',
        px: 2,
        py: 1,
        borderRadius: 8,
        backgroundColor,
        ...(isOutlined && {
          border: `1px solid ${outlined.getBorderColor(palette)}`,
          px: 1.75,
          py: 0.75,
        }),
        flexShrink: 0,
        ...sx,
      }}
    >
      <Typography variant={'small_description'} color={textColor} noWrap>
        {label}
      </Typography>
    </Stack>
  );
};
